import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { makeTranslationsSchema } from '../../common/translations/translations';
import { NotificationCategorySchema } from '../notification-category/notification-category';
import {
  NotificationChannelSchema,
  type NotificationChannel,
} from '../notification-channel/notification-channel';
import { NotificationTemplateKeySchema } from '../notification-template-key/notification-template-key';

export const TEMPLATE_TITLE_MAX_LENGTH = 120;

/**
 * A push body is truncated by both platforms well before this; the ceiling is
 * here so the in-app row — which is not truncated — stays a notification rather
 * than becoming an article. Anything longer belongs behind the deep link.
 */
export const TEMPLATE_BODY_MAX_LENGTH = 1000;

/**
 * An email can afford to explain what a lock screen cannot (P21e1). Still a
 * ceiling: a template is a notice, not an article, and the words a member wants
 * after the phone is gone are decisions, consequences, receipts and
 * appointments — a few paragraphs, never a page.
 */
export const TEMPLATE_EMAIL_BODY_MAX_LENGTH = 4000;

/**
 * A text message is billed by the segment — 160 GSM-7 characters, 70 in UCS-2
 * the moment one Arabic letter is present (P30b1). Two GSM segments is room for
 * the placeholders a template carries before rendering; the seeded copy renders
 * to one segment in either script, and the editor says so per keystroke.
 */
export const TEMPLATE_SMS_BODY_MAX_LENGTH = 320;

/** The body ceiling a channel's copy is held to. */
export function bodyMaxLengthFor(channel: NotificationChannel): number {
  switch (channel) {
    case 'email':
      return TEMPLATE_EMAIL_BODY_MAX_LENGTH;
    case 'sms':
      return TEMPLATE_SMS_BODY_MAX_LENGTH;
    default:
      return TEMPLATE_BODY_MAX_LENGTH;
  }
}

const translationSchemaFor = (bodyMax: number) =>
  z
    .object({
      title: z.string().trim().min(1).max(TEMPLATE_TITLE_MAX_LENGTH),
      body: z.string().trim().min(1).max(bodyMax),
    })
    .strict();

/**
 * Per-locale copy (N5, W7). Both locales are required — U8's parity law.
 *
 * Bounded by the **largest** ceiling, because this is the channel-agnostic shape
 * the read DTO and the update patch carry. The per-channel ceiling is applied
 * where the channel is known: {@link notificationTemplateTranslationsSchemaFor}
 * on the create DTO, and the templates service on an update, against the row's
 * own channel.
 */
export const NotificationTemplateTranslationSchema = translationSchemaFor(
  TEMPLATE_EMAIL_BODY_MAX_LENGTH,
);

export type NotificationTemplateTranslation = z.infer<typeof NotificationTemplateTranslationSchema>;

export const NotificationTemplateTranslationsSchema = makeTranslationsSchema(
  NotificationTemplateTranslationSchema,
);

/** The translations map, held to the ceiling of the channel the copy is for. */
export function notificationTemplateTranslationsSchemaFor(channel: NotificationChannel) {
  return makeTranslationsSchema(translationSchemaFor(bodyMaxLengthFor(channel)));
}

/**
 * One row of operator-owned notification copy (N17).
 *
 * **The key is the join to the code catalogue, and it is never edited.** It is
 * what `NotificationRequested` names, what a violation type's
 * `legalNoticeTemplateKey` points at, and what decides which parameters this
 * copy may interpolate — so renaming one would silently detach the copy from
 * every notice that asks for it. `category` and `channel` are stored on the row
 * (rather than only derived) because the delivery log and the admin list filter
 * on them, and a filter that has to resolve a code catalogue per row is a filter
 * the database cannot answer.
 */
export const NotificationTemplateSchema = z
  .object({
    id: UuidSchema,
    key: NotificationTemplateKeySchema,
    category: NotificationCategorySchema,
    channel: NotificationChannelSchema,
    /**
     * Inactive templates are kept, never deleted: a `notifications` row from
     * last year names this key, and a delivery log a staff member is reading
     * must still resolve it. Deactivating suppresses **future** sends — and for
     * a `bypassesPreferences` notice it is the only switch there is, which is
     * why the admin surface must say so plainly rather than offering it as a
     * tidy-up.
     */
    isActive: z.boolean(),
    title: z.string(),
    body: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type NotificationTemplate = z.infer<typeof NotificationTemplateSchema>;

/** What the admin edit form reads: every locale, not just the caller's (W7). */
export const NotificationTemplateDetailSchema = NotificationTemplateSchema.extend({
  translations: NotificationTemplateTranslationsSchema,
}).strict();

export type NotificationTemplateDetail = z.infer<typeof NotificationTemplateDetailSchema>;

/**
 * One catalogue key as the admin surface sees it — the code declaration plus
 * whether copy has been authored for it.
 *
 * This is what turns P20b's free-text `legalNoticeTemplateKey` into a picker,
 * and what the template editor lists as "notices the platform can send but
 * nobody has written yet".
 */
export const NotificationCatalogueKeySchema = z
  .object({
    key: NotificationTemplateKeySchema,
    category: NotificationCategorySchema,
    bypassesPreferences: z.boolean(),
    params: z.array(
      z
        .object({
          name: z.string(),
          kind: z.enum(['text', 'number', 'datetime']),
          required: z.boolean(),
        })
        .strict(),
    ),
    /** Which channels already have a row of copy. Empty means unauthored. */
    authoredChannels: z.array(NotificationChannelSchema),
  })
  .strict();

export type NotificationCatalogueKey = z.infer<typeof NotificationCatalogueKeySchema>;
